import React, { useMemo, useCallback, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { usePlanningStore, getCategories, COMPACTION_CONFIG } from "@store/planningStore";
import { AppointmentCategory, Appointment, CategoryConfig } from "../../types/garage/planning";
import { snapToGrid, formatTime } from "@utils/timeGrid";
import OrdreModal from "@components/modals/OrdreModal";
import LocationModal from "@components/modals/LocationModal";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";
import QuickAppointmentModal from "@components/modals/QuickAppointmentModal";
import { findOrdreForPlate, buildOrdreFromAppointment, getOrdreById } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import { vehiclesApi } from "@services/api/vehicles";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import type { Ordre } from "types/ordre";
import type { VehicleReservation } from "types/garage/vehicules";
import { PlanningContextMenu, ContextMenuState } from "@components/ui/PlanningContextMenu";

const HOURS_START = 7;
const HOURS_END = 20;
const SNAP_HOUR = 0.125;

const FALLBACK_CONFIG: CategoryConfig = { id: "rdv_divers", label: "?", color: "#818CF8", bgColor: "#E0E6FF", borderColor: "#CCD0FF", barColor: "#A5B4FC" };

interface DragPreview {
  apptId: string;
  startHour: number;
  durationHours: number;
  category: AppointmentCategory;
}

const CLICK_THRESHOLD = 10; // px — if pointer moves less, treat as click

export function TimeGridWithAppointments({ externalPreviews }: { externalPreviews?: DragPreview[] } = {}) {
  const getFilteredAppointments = usePlanningStore(
    (s) => s.getFilteredAppointments,
  );
  const activeCategories = usePlanningStore((s) => s.activeCategories);
  const updateAppointment = usePlanningStore((s) => s.updateAppointment);
  const compaction = usePlanningStore((s) => s.compaction);
  const HOUR_BLOCK_HEIGHT = COMPACTION_CONFIG[compaction].hourBlockHeight;
  const SNAP_PX = COMPACTION_CONFIG[compaction].snapPx;
  const appointments = getFilteredAppointments();

  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);
  const canMoveAppt = isOwnerOrAdmin(userData);
  const selectedDate = usePlanningStore((s) => s.selectedDate);
  const highlightOrdreId = usePlanningStore((s) => s.highlightOrdreId);

  const allCategories = useMemo(() => getCategories(orgData), [orgData]);
  const gridCategories = useMemo(() => allCategories.map((c) => c.id), [allCategories]);
  const getCategoryConfig = useCallback(
    (id: AppointmentCategory) => allCategories.find((c) => c.id === id) || FALLBACK_CONFIG,
    [allCategories],
  );
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<VehicleReservation | null>(null);
  const [hoveredApptId, setHoveredApptId] = useState<string | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);
  const [quickCreate, setQuickCreate] = useState<{ date: string; startHour: number; category: AppointmentCategory } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [pendingMoveConfirm, setPendingMoveConfirm] = useState<{
    apptId: string;
    updates: { startHour: number; durationHours: number; category: AppointmentCategory };
  } | null>(null);
  const [restitutionSaving, setRestitutionSaving] = useState(false);
  const [pendingRestitutionMove, setPendingRestitutionMove] = useState<{
    appt: Appointment;
    updates: { startHour: number; durationHours: number; category: AppointmentCategory };
    oldSeconds?: number;
    newSeconds: number;
  } | null>(null);

  const visibleCategories = gridCategories.filter((c) =>
    activeCategories.includes(c),
  );
  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  const byCategory = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    for (const cat of gridCategories) map[cat] = [];
    for (const a of appointments) {
      if (map[a.category]) map[a.category].push(a);
    }
    return map;
  }, [appointments]);

  // Compute overlap layout per category: { colIndex, totalCols } for each appointment
  const overlapLayout = useMemo(() => {
    const layout: Record<string, { colIndex: number; totalCols: number }> = {};
    for (const cat of gridCategories) {
      const appts = byCategory[cat];
      if (!appts.length) continue;
      const sorted = [...appts].sort(
        (a, b) => a.startHour - b.startHour || b.durationHours - a.durationHours,
      );
      const OVERLAP_EPS = 0.034; // ~2 min tolerance for floating-point end/start match
      let clusterStart = 0;
      let maxEnd = 0;
      for (let i = 0; i <= sorted.length; i++) {
        const isEnd = i === sorted.length || (i > 0 && sorted[i].startHour >= maxEnd - OVERLAP_EPS);
        if (isEnd && i > clusterStart) {
          const columns: Appointment[][] = [];
          for (let k = clusterStart; k < i; k++) {
            const appt = sorted[k];
            let placed = false;
            for (let col = 0; col < columns.length; col++) {
              const lastInCol = columns[col][columns[col].length - 1];
              if (lastInCol.startHour + lastInCol.durationHours <= appt.startHour + OVERLAP_EPS) {
                columns[col].push(appt);
                layout[appt.id] = { colIndex: col, totalCols: 0 };
                placed = true;
                break;
              }
            }
            if (!placed) {
              layout[appt.id] = { colIndex: columns.length, totalCols: 0 };
              columns.push([appt]);
            }
          }
          const totalCols = columns.length;
          for (let k = clusterStart; k < i; k++) {
            layout[sorted[k].id].totalCols = totalCols;
          }
          clusterStart = i;
        }
        if (i < sorted.length) {
          maxEnd = Math.max(maxEnd, sorted[i].startHour + sorted[i].durationHours);
        }
      }
    }
    return layout;
  }, [byCategory]);

  const pendingDragRef = useRef<{
    appt: Appointment;
    mode: "move" | "resize";
    startX: number;
    startY: number;
  } | null>(null);
  const dragRef = useRef<{
    apptId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origCategory: AppointmentCategory;
  } | null>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, appt: Appointment, mode: "move" | "resize") => {
      if (e.button === 2) return; // ignore right-click (context menu)
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      clickStartRef.current = { x: e.clientX, y: e.clientY };

      // Two-phase drag: store pending, only activate when threshold exceeded
      pendingDragRef.current = {
        appt,
        mode,
        startX: e.clientX,
        startY: e.clientY,
      };
    },
    [],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    // Phase 1: check if pending drag should activate
    const pending = pendingDragRef.current;
    if (pending && !dragRef.current) {
      const dx = Math.abs(e.clientX - pending.startX);
      const dy = Math.abs(e.clientY - pending.startY);
      if (dx >= CLICK_THRESHOLD || dy >= CLICK_THRESHOLD) {
        const { appt, mode } = pending;
        dragRef.current = {
          apptId: appt.id,
          mode,
          startY: pending.startY,
          origStartHour: appt.startHour,
          origDuration: appt.durationHours,
          origCategory: appt.category,
        };
        setPreview({
          apptId: appt.id,
          startHour: appt.startHour,
          durationHours: appt.durationHours,
          category: appt.category,
        });
        pendingDragRef.current = null;
      } else {
        return;
      }
    }

    // Phase 2: active drag
    const d = dragRef.current;
    if (!d) return;

    const deltaY = e.clientY - d.startY;
    const snapDelta = Math.round(deltaY / SNAP_PX);

    if (d.mode === "move") {
      const newStart = snapToGrid(
        d.origStartHour + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        HOURS_START,
        Math.min(newStart, HOURS_END - d.origDuration),
      );

      let newCategory = d.origCategory;
      if (scrollRef.current) {
        const cols =
          scrollRef.current.querySelectorAll<HTMLElement>("[data-cat-col]");
        cols.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX < rect.right) {
            newCategory = el.getAttribute(
              "data-cat-col",
            ) as AppointmentCategory;
          }
        });
      }

      setPreview({
        apptId: d.apptId,
        startHour: clamped,
        durationHours: d.origDuration,
        category: newCategory,
      });
    } else {
      const newDuration = snapToGrid(
        d.origDuration + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        SNAP_HOUR,
        Math.min(newDuration, HOURS_END - d.origStartHour),
      );
      setPreview({
        apptId: d.apptId,
        startHour: d.origStartHour,
        durationHours: clamped,
        category: d.origCategory,
      });
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const pending = pendingDragRef.current;
    const d = dragRef.current;
    const p = preview;
    const start = clickStartRef.current;

    pendingDragRef.current = null;

    // Check if this was a click (no significant movement)
    const isClick = start &&
      Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
      Math.abs(e.clientY - start.y) < CLICK_THRESHOLD;

    // Click: open the appointment (from pending move or active drag that didn't move)
    const clickApptId = pending?.appt.id || (d?.mode === "move" ? d.apptId : null);
    if (isClick && clickApptId) {
      const appt = appointments.find((a) => a.id === clickApptId);
      if (appt) {
        if (appt.reservation_id) {
          vehiclesApi.getReservation(appt.reservation_id).then(setSelectedReservation).catch(() => {});
        } else if (appt.ordre_id) {
          // Fetch fresh ordre from API so dragged times are reflected
          ordresApi.get(appt.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            setSelectedOrdre(findOrdreForPlate(appt.plate) || buildOrdreFromAppointment(appt));
          });
        } else {
          setSelectedOrdre(findOrdreForPlate(appt.plate) || buildOrdreFromAppointment(appt));
        }
      }
    } else if (d && p) {
      // Non-managers cannot move appointments
      if (!canMoveAppt) {
        dragRef.current = null;
        clickStartRef.current = null;
        setPreview(null);
        return;
      }
      const appt = appointments.find((a) => a.id === d.apptId);
      if (appt?.type === "restitution" && appt.ordre_id) {
        // Intercept restitution move — show confirmation modal
        const selectedDate = usePlanningStore.getState().selectedDate;
        const apptDate = appt.date || selectedDate;
        const oldDate = new Date(`${apptDate}T00:00:00`);
        oldDate.setHours(Math.floor(appt.startHour), Math.round((appt.startHour % 1) * 60));
        const newDate = new Date(`${apptDate}T00:00:00`);
        newDate.setHours(Math.floor(p.startHour), Math.round((p.startHour % 1) * 60));
        setPendingRestitutionMove({
          appt,
          updates: { startHour: p.startHour, durationHours: p.durationHours, category: p.category },
          oldSeconds: Math.floor(oldDate.getTime() / 1000),
          newSeconds: Math.floor(newDate.getTime() / 1000),
        });
      } else {
        // Manager move — show confirmation modal
        setPendingMoveConfirm({
          apptId: d.apptId,
          updates: { startHour: p.startHour, durationHours: p.durationHours, category: p.category },
        });
      }
    }
    dragRef.current = null;
    clickStartRef.current = null;
    setPreview(null);
  }, [preview, updateAppointment, appointments]);

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Column headers */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {visibleCategories.map((catId) => {
          const cat = getCategoryConfig(catId);
          return (
            <div
              key={catId}
              className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: "#FFFFFF",
                color: cat.color,
              }}
            >
              {cat.label === "Location" ? "Location RAC" : cat.label}
            </div>
          );
        })}
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} className="relative">
        <div className="flex flex-col">
          {hours.map((h) => (
            <div
              key={h}
              className="flex flex-row"
              style={{
                height: HOUR_BLOCK_HEIGHT,
                borderBottom: "1px solid #D9D9D9",
              }}
            >
              <div
                className="flex items-start"
                style={{
                  width: hourLabelWidth,
                  paddingLeft: 7,
                  paddingTop: 2,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                  flexShrink: 0,
                }}
              >
                {h}h
              </div>

              <div
                className="flex flex-col"
                style={{
                  width: minuteColWidth,
                  borderLeft: "1px solid #D9D9D9",
                  borderRight: "1px solid #D9D9D9",
                  flexShrink: 0,
                }}
              >
                {["00", "15", "30", "45"].map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-center"
                    style={{
                      height: 32,
                      borderBottom: "1px solid #D9D9D9",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#A1A1A1",
                    }}
                  >
                    {m}
                  </div>
                ))}
              </div>

              {/* Category columns */}
              {visibleCategories.map((catId) => {
                const colAppts = (byCategory[catId] || []).filter(
                  (a) => a.startHour >= h && a.startHour < h + 1,
                );

                const ghostInCell =
                  preview &&
                  preview.category === catId &&
                  preview.startHour >= h &&
                  preview.startHour < h + 1
                    ? preview
                    : null;

                const extGhosts = (externalPreviews || []).filter(
                  (ep) => ep.category === catId && ep.startHour >= h && ep.startHour < h + 1,
                );

                return (
                  <div
                    key={catId}
                    data-cat-col={catId}
                    className="flex-1 relative cursor-pointer"
                    style={{ borderRight: "1px solid #D9D9D9" }}
                    onClick={(e) => {
                      // Only trigger if clicking empty space (not an appointment)
                      if ((e.target as HTMLElement).closest("[data-appt]")) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const relY = e.clientY - rect.top;
                      const clickedHour = snapToGrid(h + (relY / HOUR_BLOCK_HEIGHT), SNAP_HOUR);
                      setQuickCreate({ date: selectedDate, startHour: clickedHour, category: catId });
                    }}
                  >
                    {[0, 1, 2, 3].map((q) => (
                      <div
                        key={q}
                        style={{
                          height: 32,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            height: 16,
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                        <div
                          style={{
                            height: 16,
                            background: "#F4F4F4",
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                      </div>
                    ))}

                    {/* Ghost preview (drag) */}
                    {ghostInCell &&
                      (() => {
                        const config = getCategoryConfig(ghostInCell.category);
                        const topPx =
                          ((ghostInCell.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (ghostInCell.durationHours / SNAP_HOUR) * SNAP_PX;
                        return (
                          <div
                            className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: config.bgColor,
                              border: `2px dashed ${config.color}`,
                              borderRadius: 5,
                              margin: "0 2px",
                              zIndex: 40,
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: config.color,
                                lineHeight: "100%",
                              }}
                            >
                              {formatTime(ghostInCell.startHour)} –{" "}
                              {formatTime(
                                ghostInCell.startHour +
                                  ghostInCell.durationHours,
                              )}
                            </span>
                          </div>
                        );
                      })()}

                    {/* External previews (debug) */}
                    {extGhosts.map((eg, idx) => {
                      const config = getCategoryConfig(eg.category);
                      const topPx = ((eg.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx = (eg.durationHours / SNAP_HOUR) * SNAP_PX;
                      return (
                        <div
                          key={`ext-${idx}`}
                          className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                          style={{
                            top: topPx,
                            height: Math.max(heightPx, 20),
                            background: config.bgColor,
                            border: `2px dashed ${config.color}`,
                            borderRadius: 5,
                            margin: "0 2px",
                            zIndex: 40,
                            opacity: 0.7,
                          }}
                        >
                          <span
                            className="text-h4 font-medium"
                            style={{ color: config.color, lineHeight: "100%" }}
                          >
                            {formatTime(eg.startHour)} – {formatTime(eg.startHour + eg.durationHours)}
                          </span>
                        </div>
                      );
                    })}

                    {/* Actual appointments */}
                    {colAppts.map((appt) => {
                      const config = getCategoryConfig(appt.category);
                      const topPx =
                        ((appt.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx =
                        (appt.durationHours / SNAP_HOUR) * SNAP_PX;
                      const isDragSource = preview?.apptId === appt.id;
                      const isHovered = hoveredApptId === appt.id;
                      const isHighlighted = highlightOrdreId != null && appt.ordre_id === highlightOrdreId;
                      const ol = overlapLayout[appt.id] || { colIndex: 0, totalCols: 1 };
                      const widthPct = `calc(${100 / ol.totalCols}% - 4px)`;
                      const leftPct = `calc(${(ol.colIndex / ol.totalCols) * 100}% + 2px)`;
                      return (
                        <div
                          key={appt.id}
                          data-appt
                          className={`absolute flex flex-col${isHighlighted ? " highlight-pulse" : ""}`}
                          style={{
                            top: topPx,
                            left: leftPct,
                            width: widthPct,
                            height: Math.max(heightPx, 14),
                            background: config.bgColor,
                            border: isHighlighted ? "2px solid #6366F1" : `1px solid ${config.borderColor}`,
                            boxShadow: isHighlighted
                              ? "0px 0px 12px rgba(99, 102, 241, 0.5)"
                              : isHovered
                              ? "0px 4px 12px rgba(0, 0, 0, 0.25)"
                              : "none",
                            borderRadius: 5,
                            padding: heightPx < 28 ? "1px 4px" : "3px 4px",
                            overflow: "hidden",
                            zIndex: isDragSource ? 5 : isHovered ? 15 : 10,
                            cursor: canMoveAppt ? "grab" : "pointer",
                            userSelect: "none",
                            opacity: isDragSource ? 0.35 : 1,
                            transition: isDragSource
                              ? "opacity 0.15s"
                              : "box-shadow 0.15s",
                          }}
                          onPointerDown={(e) =>
                            handlePointerDown(e, appt, "move")
                          }
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!appt.ordre_id) return;
                            const ordre = getOrdreById(appt.ordre_id);
                            if (!ordre) return;
                            setContextMenu({ x: e.clientX, y: e.clientY, ordreId: ordre.id, currentStatus: ordre.status });
                          }}
                          onMouseEnter={() => setHoveredApptId(appt.id)}
                          onMouseLeave={() => setHoveredApptId(null)}
                        >
                          {/* Hover tooltip */}
                          {isHovered && !preview && (
                            <div
                              className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                              style={{ whiteSpace: "nowrap", minWidth: 140 }}
                              ref={(el) => {
                                if (!el) return;
                                const parent = el.parentElement;
                                if (!parent) return;
                                const rect = parent.getBoundingClientRect();
                                const tipW = el.offsetWidth;
                                const fitsRight = rect.right + 8 + tipW <= window.innerWidth;
                                el.style.top = `${rect.top}px`;
                                el.style.left = fitsRight ? `${rect.right + 8}px` : `${rect.left - tipW - 8}px`;
                              }}
                            >
                              <div className="text-h4 font-medium">{appt.clientName}</div>
                              {appt.plate && <div className="text-h5 text-neutral-300">{appt.plate}</div>}
                              {(() => {
                                const thisDate = appt.date || selectedDate;
                                const fmtDate = (d: string) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
                                const pair = appt.ordre_id ? appointments.find((a) => a.ordre_id === appt.ordre_id && a.id !== appt.id) : null;
                                const receptionAppt = appt.type === "reception" ? appt : pair;
                                const restitutionAppt = appt.type === "restitution" ? appt : pair;
                                return (
                                  <>
                                    {receptionAppt && (
                                      <div className="text-h5 text-neutral-300">
                                        Réception : {fmtDate(receptionAppt.date || thisDate)} {formatTime(receptionAppt.startHour)} — {formatTime(receptionAppt.startHour + receptionAppt.durationHours)}
                                      </div>
                                    )}
                                    {restitutionAppt && (
                                      <div className="text-h5 text-neutral-300">
                                        Restitution : {fmtDate(restitutionAppt.date || thisDate)} {formatTime(restitutionAppt.startHour)} — {formatTime(restitutionAppt.startHour + restitutionAppt.durationHours)}
                                      </div>
                                    )}
                                    {!receptionAppt && !restitutionAppt && (
                                      <div className="text-h5 text-neutral-300">
                                        {fmtDate(thisDate)} {formatTime(appt.startHour)} — {formatTime(appt.startHour + appt.durationHours)}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              <div className="text-h5 text-neutral-400 mt-1">Cliquer pour modifier</div>
                            </div>
                          )}
                          <div
                            className="flex flex-row items-center"
                            style={{ gap: 3, minWidth: 0, overflow: "hidden", flex: 1 }}
                          >
                            {(appt.hasCarIcon || appt.hasMailIcon) && (
                              <Icon1
                                name={appt.type === "restitution" ? "car-restitution" : "car-reception"}
                                size={12}
                                color={config.color}
                              />
                            )}
                            <span
                              className="font-medium"
                              style={{
                                fontSize: heightPx < 28 ? 10 : 12,
                                lineHeight: "110%",
                                color: config.color,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {appt.clientName}
                              {heightPx >= 28 && appt.plate ? ` - ${appt.plate}` : ""}
                            </span>
                          </div>
                          {heightPx >= 24 && (
                            <div
                              className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                              style={{ height: 8, cursor: "ns-resize" }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                handlePointerDown(e, appt, "resize");
                              }}
                            >
                              <div
                                style={{
                                  width: 20,
                                  height: 2,
                                  borderRadius: 1,
                                  background: config.color,
                                  opacity: 0.4,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
          onDelete={() => setSelectedOrdre(null)}
        />
      )}
      {selectedReservation && (
        <LocationModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onSave={() => setSelectedReservation(null)}
        />
      )}
      {pendingRestitutionMove && (
        <RestitutionChangeConfirmModal
          oldSeconds={pendingRestitutionMove.oldSeconds}
          newSeconds={pendingRestitutionMove.newSeconds}
          onClose={() => setPendingRestitutionMove(null)}
          saving={restitutionSaving}
          onConfirm={async (comment, notify) => {
            setRestitutionSaving(true);
            try {
              const { appt, updates } = pendingRestitutionMove;
              updateAppointment(appt.id, updates);
              if (appt.ordre_id) {
                await ordresApi.notifyRestitutionChange(appt.ordre_id, {
                  old_date_seconds: pendingRestitutionMove.oldSeconds,
                  new_date_seconds: pendingRestitutionMove.newSeconds,
                  comment: comment || undefined,
                  notify,
                });
              }
              setPendingRestitutionMove(null);
            } catch (e) {
              console.error("Error notifying restitution change:", e);
            } finally {
              setRestitutionSaving(false);
            }
          }}
        />
      )}
      {pendingMoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-[340px] flex flex-col gap-4 shadow-xl">
            <h3 className="text-[15px] font-semibold text-neutral-800 m-0">Déplacer ce rendez-vous ?</h3>
            <p className="text-[13px] text-neutral-500 m-0">
              Vous êtes sur le point de modifier l'horaire de ce rendez-vous.
            </p>
            <div className="flex flex-row gap-2 justify-end">
              <button
                onClick={() => setPendingMoveConfirm(null)}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  updateAppointment(pendingMoveConfirm.apptId, pendingMoveConfirm.updates);
                  setPendingMoveConfirm(null);
                }}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-[#5C6BFF] hover:bg-[#4B5AEE] transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      {quickCreate && (
        <QuickAppointmentModal
          date={quickCreate.date}
          startHour={quickCreate.startHour}
          category={quickCreate.category}
          onClose={() => setQuickCreate(null)}
          onCreated={(ordreId) => {
            ordresApi.get(ordreId).then((ordre) => setSelectedOrdre(ordre)).catch(() => {});
          }}
        />
      )}
      {contextMenu && (
        <PlanningContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
      )}
    </div>
  );
}
